import { useDownloadFile } from '@/hooks/useApi'
import type { BlobRequestOptions } from '@/lib/base-api'
import { saveBlob } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

const downloadedFiles = new Map<number, Promise<{ blob: Blob; filename: string }>>()

interface FileActionOptions {
  fileId?: number
  file?: File
  fileName: string
}

export function useFileAction({ fileId, file, fileName }: FileActionOptions) {
  const t = useTranslations('common')
  const [progress, setProgress] = useState<number>()
  const downloadController = useRef<AbortController | null>(null)
  const { mutateAsync: downloadFile } = useDownloadFile()

  const getFile = useCallback(
    async (options?: BlobRequestOptions) => {
      if (file) return { blob: file, filename: file.name }
      if (fileId && fileId > 0) {
        // Downloads own their request so cancelling cannot abort another attachment's preview.
        if (options) return downloadFile({ fileId, ...options })
        const cachedFile = downloadedFiles.get(fileId)
        if (cachedFile) return cachedFile

        const fileRequest = downloadFile({ fileId }).catch((error) => {
          downloadedFiles.delete(fileId)
          throw error
        })
        downloadedFiles.set(fileId, fileRequest)
        return fileRequest
      }
      throw new Error('File is not available')
    },
    [file, fileId, downloadFile],
  )

  useEffect(() => () => downloadController.current?.abort(), [file, fileId])

  const download = async () => {
    if (downloadController.current) return
    const controller = new AbortController()
    downloadController.current = controller
    setProgress(0)
    try {
      const { blob } = await getFile({ signal: controller.signal, onDownloadProgress: setProgress })
      if (!controller.signal.aborted) saveBlob(blob, fileName)
    } catch {
      if (!controller.signal.aborted) {
        toast.error(t('fileDownloadError'), { description: t('fileDownloadErrorDescription') })
      }
    } finally {
      downloadController.current = null
      setProgress(undefined)
    }
  }

  const cancelDownload = () => downloadController.current?.abort()

  return { isPending: progress !== undefined, progress, download, cancelDownload, getFile }
}
